// Third-party
import React, { Component } from 'react';
import { connect } from 'react-redux';

// Components
import DataTable from '../../../components/data/DataTable';
import { PrimaryButton, IconButton } from '../../../components/buttons';
import { AddEnterpriseGroupModal } from '../../../components/modals';
import { DestructiveModal } from '@capintel/component-lib/destructive-modal';
import { Stack } from '@capintel/component-lib/stack';
import { SearchInput } from '../../../components/inputs';
import { GenericEmptyState } from '../../../components/misc';

// Utils
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';
import { searchObjectsOnFields } from '../../../../utils/search';
import { isEnterpriseGroup, ENTERPRISE_GROUP_NAME } from '../../../../utils/enterprise';
import { ReduxState } from '../../../../redux/modules/types';
import { addEnterpriseGroup, deleteEnterpriseGroup } from '../../../../redux/modules/enterprise/admin/groups';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '../../../../utils/permissions';
import EnterpriseGroup from '@/types/enterprise-group';
import EnterpriseDetails from '@/types/enterprise-detail-info';
import EnterpriseAdminDetails from '@/types/enterprise-admin-details';
import Nullable from '@/types/nullable';
import User from '@/types/user';

type State = {
  showAddGroupModal: boolean;
  showDeleteGroupsModal: boolean;
  groupsToDelete: EnterpriseGroup[];
  query: string;
  expanded?: EnterpriseGroup;
};

type DispatchProps = {
  addEnterpriseGroup: (name: string) => Promise<EnterpriseGroup>;
  deleteEnterpriseGroup: (groupId: string) => Promise<void>;
};

type StateProps = {
  enterprise: EnterpriseDetails;
  adminDetails: EnterpriseAdminDetails;
  user?: Nullable<User>;
};

type ParentProps = {
  onRowClick?: (id: string) => void;
};

type Props = StateProps & ParentProps & DispatchProps;

class EnterpriseGroups extends Component<Props, State> {
  readonly state: State = {
    showAddGroupModal: false,
    showDeleteGroupsModal: false,
    groupsToDelete: [],
    query: '',
  };

  removeSelectedGroups = () => {
    const { user } = this.props;
    const { groupsToDelete } = this.state;

    if (!user) {
      return;
    }

    this.hideDeleteGroupsModal();

    for (const group of groupsToDelete) {
      this.props.deleteEnterpriseGroup(group._id);
    }
  };

  deleteGroupById = (id: string) => {
    this.props.deleteEnterpriseGroup(id);
  };

  allSelectableItems = () => {
    const { adminDetails } = this.props;
    const localGroups = (adminDetails && adminDetails.groups) || [];

    const { query } = this.state;
    if (query !== '') {
      return this.filterGroupsFromQuery(localGroups, query).filter(x => !isEnterpriseGroup(x));
    }
    return localGroups.filter(x => !isEnterpriseGroup(x));
  };

  showDeleteGroupsModal = (groupsToDelete: EnterpriseGroup[]) => {
    this.setState(() => ({
      showDeleteGroupsModal: true,
      groupsToDelete,
    }));
  };

  showAddGroupModal = () => {
    this.setState({ showAddGroupModal: true });
  };

  hideAddGroupModal = () => {
    this.setState({ showAddGroupModal: false });
  };

  hideDeleteGroupsModal = () => {
    this.setState({ showDeleteGroupsModal: false });
  };

  filterGroupsFromQuery = (groups: EnterpriseGroup[], query: string) => {
    if (!query) {
      return groups;
    }

    const searchableFields = ['name'];

    // Generate fullname for search purposes.
    const objs = groups.map(m => ({
      ...m,
      fullname: m.name,
    }));

    return searchObjectsOnFields(query, objs, searchableFields, false);
  };

  onGroupsSearch = (query: string) => {
    this.setState({ query });
  };

  getColumns = (): any[] => {
    const { user } = this.props;
    const hasGroupAdminPermission = userHasPermission(user, PERMISSIONS.ENTERPRISE_ADMIN_GROUPS);
    const hasMemberAdminPermission = userHasPermissionEnabled(
      user,
      PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS,
      PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_ADD,
      PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS_REMOVE,
    );

    const columns = [
      {
        Header: formatMessage('Admin.Enterprise.Fields.GroupName'),
        accessor: 'name',
        className: 'enterprise-group-name',
        width: hasMemberAdminPermission ? 150 : undefined,
      },
      {
        id: 'colMembers',
        Header: formatMessage('Admin.Enterprise.TeamMembers'),
        className: 'enterprise-group-name',
        accessor: (group: EnterpriseGroup) => formatMessage('Admin.Enterprise.TeamMembersCount', { count: group.members.length }),
        show: hasMemberAdminPermission,
      },
      {
        accessor: '_id',
        className: 'icon-cell',
        Cell: ({ original }: { original: EnterpriseGroup }) => (
          <IconButton
            icon="ds-delete"
            onClick={() => {
              this.showDeleteGroupsModal([original]);
            }}
          />
        ),
        width: 48,
        show: hasGroupAdminPermission,
      },
    ];

    return columns;
  };

  onAddGroupToEnterprise = (name: string) =>
    // Reload enterprise's data after adding a new group
    this.props.addEnterpriseGroup(name).then(
      newGroup =>
        // TODO - get groups
        // this.props.getEnterprise(enterprise._id);
        newGroup,
    );

  onRemoveGroupFromEnterprise = (groupId: string) => this.props.deleteEnterpriseGroup(groupId);

  render() {
    const { showAddGroupModal, showDeleteGroupsModal, query } = this.state;
    const { adminDetails, onRowClick, user } = this.props;

    const localGroups = (adminDetails && adminDetails.groups.filter(g => g.name !== ENTERPRISE_GROUP_NAME)) || [];
    const filteredGroups = this.filterGroupsFromQuery(localGroups, query);
    const hasGroups = localGroups && localGroups.length > 0;

    return (
      <>
        <div className="enterprise-groups">
          <h3>
            <FormattedMessage id="Admin.Enterprise.Groups.Title" />
          </h3>

          <div className="section-headers d-flex justify-content-between">
            <SearchInput
              className="settings-search"
              value={this.state.query}
              onChange={this.onGroupsSearch}
              placeholder={formatMessage('Admin.Enterprise.SearchGroups')}
            />
            <PrimaryButton onClick={this.showAddGroupModal} disabled={!userHasPermission(user, PERMISSIONS.ENTERPRISE_ADMIN_GROUPS)}>
              <i className="icon-plus" />
              <FormattedMessage id="Admin.Enterprise.AddGroup" />
            </PrimaryButton>
          </div>

          {hasGroups && (
            <DataTable data={filteredGroups} className="results-table flex-grow-1" columns={this.getColumns()} canSelect={false} onRowClick={onRowClick} />
          )}

          {localGroups.length === 0 && (
            <div className="empty-results">
              <GenericEmptyState header={formatMessage('Admin.Enterprise.NoGroups.Title')} body={formatMessage('Admin.Enterprise.NoGroups.Text')} />
            </div>
          )}

          {localGroups.length > 0 && !hasGroups && (
            <div className="empty-results">
              <h1>
                <FormattedMessage id="Admin.Enterprise.NoResults.Title" />
              </h1>
              <span className="empty-results-text">
                <FormattedMessage id="Admin.Enterprise.NoResults.Text" />
              </span>
            </div>
          )}
        </div>

        <AddEnterpriseGroupModal show={showAddGroupModal} onCancel={this.hideAddGroupModal} onSave={this.onAddGroupToEnterprise} />

        <DestructiveModal
          open={showDeleteGroupsModal}
          onOpenChange={open => !open && this.hideDeleteGroupsModal()}
          title={formatMessage('Admin.Enterprise.DeleteGroupsModal.Title')}
          content={
            <Stack gap="150">
              <FormattedMessage id="Admin.Enterprise.DeleteGroupsModal.Body" />
              <ul>
                {this.state.groupsToDelete.map(g => (
                  <li key={g.name}>{g.name}</li>
                ))}
              </ul>
              <FormattedMessage id="Admin.Enterprise.DeleteGroupsModal.Confirmation" />
            </Stack>
          }
          confirmButtonText={formatMessage('Modal.Default.Confirm')}
          cancelButtonText={formatMessage('Common.Cancel')}
          onConfirm={this.removeSelectedGroups}
          onCancel={this.hideDeleteGroupsModal}
        />
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  user: state.auth.user,
  adminDetails: state.enterprise.admin,
  enterprise: state.enterprise.details,
});

export default connect(mapStateToProps, {
  addEnterpriseGroup,
  deleteEnterpriseGroup,
})(EnterpriseGroups);
